import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Bell, CheckCircle2, Copy, Users } from 'lucide-react';
import { format } from 'date-fns';

import { getApiUrl } from '@/lib/utils';

const API_URL = getApiUrl();

function AdminNotifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [notifRes, paymentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/notifications`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }),
        axios.get(`${API_URL}/api/admin/pending-payments`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        })
      ]);
      setNotifications(notifRes.data);
      setPendingPayments(paymentsRes.data);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markNotified = async (notificationId) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/notifications/${notificationId}/mark-notified`,
        {},
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success('Marked as notified');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const verifyPayment = async (paymentId, approve) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/verify-payment/${paymentId}`,
        { approved: approve },
        {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
        }
      );
      toast.success(approve ? 'Payment verified!' : 'Payment rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to verify payment');
    }
  };

  const copyPhoneNumbers = () => {
    const phones = notifications
      .filter(n => !n.notified)
      .map(n => n.user?.phone)
      .filter(Boolean)
      .join('\n');
    navigator.clipboard.writeText(phones);
    toast.success('Phone numbers copied!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  const unnotified = notifications.filter(n => !n.notified);

  return (
    <div className="min-h-screen bg-slate-50 pb-6">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-[#0F172A] text-white pt-[env(safe-area-inset-top)]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button
            onClick={() => navigate('/admin')}
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            <h1 className="text-lg font-bold">NOTIFICATIONS & PAYMENTS</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Waitlist Promotions */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-[#0F172A]">Players to Notify ({unnotified.length})</h2>
            {unnotified.length > 0 && (
              <Button
                onClick={copyPhoneNumbers}
                variant="outline"
                size="sm"
                className="border-[#E63946] text-[#E63946]"
                data-testid="copy-phones-btn"
              >
                <Copy className="w-4 h-4 mr-1" />
                Copy Phones
              </Button>
            )}
          </div>

          {unnotified.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">No pending notifications</p>
            </div>
          ) : (
            <div className="space-y-3">
              {unnotified.map((notif) => (
                <NotificationCard
                  key={notif.notification_id}
                  notification={notif}
                  onMarkNotified={markNotified}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pending Payment Verifications */}
        <div>
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">
            Payment Verifications ({pendingPayments.length})
          </h2>

          {pendingPayments.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-3" />
              <p className="text-slate-600">All payments verified!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {pendingPayments.map((payment) => (
                <PaymentVerificationCard
                  key={payment.payment_id}
                  payment={payment}
                  onVerify={verifyPayment}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationCard({ notification, onMarkNotified }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border-l-4 border-l-slate-500 border-y border-r border-slate-200 p-5" data-testid={`notification-${notification.notification_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="font-bold text-[#0F172A] text-lg">{notification.user?.name}</p>
          <p className="text-sm text-slate-600 mt-1">{notification.user?.phone}</p>
          <div className="mt-2 p-3 bg-slate-50 rounded-lg">
            <p className="text-sm font-semibold text-slate-900">{notification.message}</p>
            <p className="text-xs text-slate-700 mt-1">
              {notification.match && format(new Date(notification.match.date), 'dd MMM yyyy')}
            </p>
          </div>
        </div>
        <Button
          onClick={() => onMarkNotified(notification.notification_id)}
          size="sm"
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="mark-notified-btn"
        >
          <CheckCircle2 className="w-4 h-4 mr-1" />
          Notified
        </Button>
      </div>
    </div>
  );
}

function PaymentVerificationCard({ payment, onVerify }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5" data-testid={`payment-verification-${payment.payment_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <p className="font-bold text-[#0F172A]">{payment.user?.name}</p>
            <p className="text-2xl font-bold text-[#E63946]">₹{payment.amount.toFixed(0)}</p>
          </div>
          <p className="text-sm text-slate-600">{payment.user?.email}</p>
          <div className="mt-2 p-3 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-700">
              <span className="font-semibold">Match:</span>{' '}
              {payment.match?.match_type === 'saturday' ? 'Saturday' : 'Sunday'} -{' '}
              {payment.match?.date && format(new Date(payment.match.date), 'dd MMM yyyy')}
            </p>
            <p className="text-sm text-slate-700 mt-1">
              <span className="font-semibold">Transaction Ref:</span> {payment.transaction_ref}
            </p>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mt-4">
        <Button
          onClick={() => onVerify(payment.payment_id, true)}
          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
          data-testid="approve-payment-btn"
        >
          Approve
        </Button>
        <Button
          onClick={() => onVerify(payment.payment_id, false)}
          variant="outline"
          className="flex-1 border-red-500 text-red-600 hover:bg-red-50"
          data-testid="reject-payment-btn"
        >
          Reject
        </Button>
      </div>
    </div>
  );
}

export default AdminNotifications;